import { useEffect, useState } from "react";
import { Plus, FileText, Trash2, CheckCircle2, SlidersHorizontal, Wallet } from "lucide-react";
import { api, apiError, descargarPdf } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Empty } from "@/components/Common";
import { CierreNomina } from "@/components/nomina/CierreNomina";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const hoy = () => new Date().toISOString().slice(0, 10);
const primerDia = () => `${new Date().toISOString().slice(0, 8)}01`;

const ajusteVacio = {
  horas_extras: "", valor_hora_extra: "", bonificaciones: "", otros_ingresos: "",
  prestamos: "", avances: "", ausencias: "", otros_descuentos: "", nota: "",
};

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));

export const PeriodosPanel = ({ periodos, onListo }) => {
  const [nuevo, setNuevo] = useState(null);
  const [abierto, setAbierto] = useState(null);

  const crear = async () => {
    try {
      const { data } = await api.post("/nomina/periodos", {
        tipo: nuevo.tipo, desde: nuevo.desde, hasta: nuevo.hasta,
        fecha_pago: nuevo.fecha_pago || null, nombre: nuevo.nombre || null,
      });
      toast.success(`Nómina generada para ${data.totales.empleados} empleado(s)`);
      setNuevo(null);
      setAbierto(data.id);
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const borrar = async (p) => {
    try {
      await api.delete(`/nomina/periodos/${p.id}`);
      toast.success("Nómina eliminada");
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="panel-periodos">
      <div className="mb-6 flex justify-end">
        <Button className="rounded-full" data-testid="nueva-nomina"
                onClick={() => setNuevo({ tipo: "mensual", desde: primerDia(), hasta: hoy(),
                                          fecha_pago: hoy(), nombre: "" })}>
          <Plus className="mr-2 h-4 w-4" /> Generar nómina
        </Button>
      </div>

      <div className="space-y-3" data-testid="lista-periodos">
        {periodos.map((p) => (
          <article key={p.id} className="lift rounded-lg border border-border/60 bg-card p-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-base font-semibold">{p.nombre}</h3>
                <p className="text-xs text-muted-foreground">
                  {fecha(p.desde)} al {fecha(p.hasta)} · {p.totales?.empleados || 0} empleado(s)
                  {p.fecha_pago ? ` · pago ${fecha(p.fecha_pago)}` : ""}
                </p>
              </div>
              <Badge variant={p.estado === "pagada" ? "secondary" : "outline"}
                     data-testid={`estado-nomina-${p.id}`}>
                {p.estado}
              </Badge>
              <span className="font-mono text-sm font-semibold" data-testid={`neto-nomina-${p.id}`}>
                {money(p.totales?.neto)}
              </span>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-full"
                        onClick={() => setAbierto(p.id)} data-testid={`abrir-nomina-${p.id}`}>
                  <SlidersHorizontal className="mr-1 h-3 w-3" /> Ver y ajustar
                </Button>
                <Button size="sm" variant="outline" className="rounded-full"
                        onClick={() => descargarPdf(`/nomina/periodos/${p.id}/pdf`, {},
                                                    `Nomina-${p.desde}.pdf`)}
                        data-testid={`pdf-nomina-${p.id}`}>
                  <FileText className="mr-1 h-3 w-3" /> PDF
                </Button>
                {p.estado !== "pagada" && (
                  <Button size="sm" variant="ghost" onClick={() => borrar(p)}
                          data-testid={`borrar-nomina-${p.id}`}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          </article>
        ))}
        {periodos.length === 0 && (
          <Empty testId="periodos-vacios">
            Aún no ha generado ninguna nómina. Cree el periodo y el sistema calcula AFP, SFS e ISR.
          </Empty>
        )}
      </div>

      <Dialog open={!!nuevo} onOpenChange={(o) => !o && setNuevo(null)}>
        <DialogContent data-testid="modal-nueva-nomina">
          <DialogHeader>
            <DialogTitle className="font-display">Generar nómina</DialogTitle>
            <DialogDescription>
              Se incluyen todos los empleados activos con el cálculo automático de ley.
            </DialogDescription>
          </DialogHeader>
          {nuevo && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Periodicidad</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={nuevo.tipo} data-testid="nomina-tipo"
                        onChange={(e) => setNuevo({ ...nuevo, tipo: e.target.value })}>
                  <option value="mensual">Mensual (salario completo)</option>
                  <option value="quincenal">Quincenal (50% del salario)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Desde</Label>
                <Input type="date" value={nuevo.desde} data-testid="nomina-desde"
                       onChange={(e) => setNuevo({ ...nuevo, desde: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Hasta</Label>
                <Input type="date" value={nuevo.hasta} data-testid="nomina-hasta"
                       onChange={(e) => setNuevo({ ...nuevo, hasta: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Fecha de pago</Label>
                <Input type="date" value={nuevo.fecha_pago} data-testid="nomina-fecha-pago"
                       onChange={(e) => setNuevo({ ...nuevo, fecha_pago: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Nombre (opcional)</Label>
                <Input value={nuevo.nombre} data-testid="nomina-nombre"
                       onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
              </div>
              <Button className="rounded-full py-6 sm:col-span-2" onClick={crear}
                      data-testid="confirmar-nomina">
                Generar nómina
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DetallePeriodo pid={abierto} onClose={() => setAbierto(null)} onListo={onListo} />
    </div>
  );
};

const DetallePeriodo = ({ pid, onClose, onListo }) => {
  const [per, setPer] = useState(null);
  const [ajuste, setAjuste] = useState(null);
  const [cerrando, setCerrando] = useState(false);

  const cargar = () => pid && api.get(`/nomina/periodos/${pid}`).then((r) => setPer(r.data));

  useEffect(() => { setPer(null); cargar(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardarAjuste = async () => {
    try {
      await api.put(`/nomina/periodos/${pid}/empleados/${ajuste.empleado_id}`, {
        horas_extras: num(ajuste.horas_extras),
        valor_hora_extra: ajuste.valor_hora_extra === "" ? null : num(ajuste.valor_hora_extra),
        bonificaciones: num(ajuste.bonificaciones),
        otros_ingresos: num(ajuste.otros_ingresos),
        prestamos: num(ajuste.prestamos),
        avances: num(ajuste.avances),
        ausencias: num(ajuste.ausencias),
        otros_descuentos: num(ajuste.otros_descuentos),
        nota: ajuste.nota || null,
      });
      toast.success("Volante recalculado");
      setAjuste(null);
      cargar();
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const pagarUno = async (l) => {
    try {
      await api.post(`/nomina/periodos/${pid}/empleados/${l.empleado_id}/pagar`,
                     { empleado_id: l.empleado_id, metodo: "transferencia", enviar_aviso: true });
      toast.success(`Pago de ${l.nombre} registrado por ${money(l.neto)}`);
      cargar();
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const t = per?.totales || {};

  return (
    <>
      <Dialog open={!!pid} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto" data-testid="modal-detalle-nomina">
          <DialogHeader>
            <DialogTitle className="font-display">{per?.nombre || "Nómina"}</DialogTitle>
            <DialogDescription>
              {per && `${fecha(per.desde)} al ${fecha(per.hasta)} · ${per.estado}`}
            </DialogDescription>
          </DialogHeader>
          {per && (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-4">
                {[["Devengado", t.total_ingresos], ["Deducciones", t.total_deducciones],
                  ["Neto a pagar", t.neto], ["Costo con aportes", t.costo_empleador]].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-border/60 bg-card p-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{l}</p>
                    <p className="mt-2 font-display text-lg font-bold">{money(v)}</p>
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto rounded-lg border border-border/60">
                <table className="w-full min-w-[860px] text-sm" data-testid="tabla-nomina">
                  <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Empleado</th>
                      <th className="px-4 py-3">Devengado</th>
                      <th className="px-4 py-3">AFP</th>
                      <th className="px-4 py-3">SFS</th>
                      <th className="px-4 py-3">ISR</th>
                      <th className="px-4 py-3">Otros desc.</th>
                      <th className="px-4 py-3">Neto</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {per.lineas.map((l) => (
                      <tr key={l.empleado_id} className="border-t border-border/60">
                        <td className="px-4 py-3">
                          <span className="block font-medium">{l.nombre}</span>
                          <span className="block text-xs text-muted-foreground">
                            {l.cargo}{l.horas_trabajadas ? ` · ${l.horas_trabajadas} h ponchadas` : ""}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">{money(l.total_ingresos)}</td>
                        <td className="px-4 py-3 font-mono text-xs">{money(l.afp)}</td>
                        <td className="px-4 py-3 font-mono text-xs">{money(l.sfs)}</td>
                        <td className="px-4 py-3 font-mono text-xs">{money(l.isr)}</td>
                        <td className="px-4 py-3 font-mono text-xs">
                          {money(l.prestamos + l.avances + l.ausencias + l.otros_descuentos)}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs font-semibold">{money(l.neto)}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap justify-end gap-2">
                            {l.pagada ? (
                              <Badge variant="secondary" data-testid={`pagado-${l.empleado_id}`}>
                                pagado · {l.metodo_pago}
                              </Badge>
                            ) : per.estado !== "pagada" && (
                              <Button size="sm" variant="outline" className="rounded-full"
                                      onClick={() => pagarUno(l)}
                                      data-testid={`pagar-${l.empleado_id}`}>
                                <Wallet className="mr-1 h-3 w-3" /> Pagar
                              </Button>
                            )}
                            {per.estado !== "pagada" && !l.pagada && (
                              <Button size="sm" variant="outline" className="rounded-full"
                                      data-testid={`ajustar-${l.empleado_id}`}
                                      onClick={() => setAjuste({
                                        ...ajusteVacio, empleado_id: l.empleado_id, nombre: l.nombre,
                                        horas_extras: l.horas_extras || "",
                                        bonificaciones: l.bonificaciones || "",
                                        prestamos: l.prestamos || "", avances: l.avances || "",
                                        ausencias: l.ausencias || "",
                                        otros_descuentos: l.otros_descuentos || "",
                                        otros_ingresos: l.otros_ingresos || "",
                                        nota: l.nota || "",
                                      })}>
                                Ajustar
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="rounded-full"
                                    data-testid={`volante-${l.empleado_id}`}
                                    onClick={() => descargarPdf(
                                      `/nomina/periodos/${per.id}/volante/${l.empleado_id}`, {},
                                      `Volante-${l.nombre}.pdf`)}>
                              <FileText className="mr-1 h-3 w-3" /> Volante
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-xs text-muted-foreground" data-testid="aportes-empleador">
                Aportes del empleador: AFP {money(t.aportes?.afp)} · SFS {money(t.aportes?.sfs)} ·
                Riesgos laborales {money(t.aportes?.riesgos)} · INFOTEP {money(t.aportes?.infotep)} ·
                Total {money(t.aportes?.total)}
              </p>

              {per.estado !== "pagada" && (
                <Button className="w-full rounded-full py-6" onClick={() => setCerrando(true)}
                        data-testid="cerrar-nomina">
                  <CheckCircle2 className="mr-2 h-4 w-4" /> Cerrar nómina y registrar los pagos
                </Button>
              )}
              {per.estado === "pagada" && (
                <p className="text-xs text-muted-foreground" data-testid="nomina-cerrada">
                  Cerrada por {per.cerrado_por} · pago {fecha(per.fecha_pago)} ·
                  {" "}{money(per.total_pagado ?? t.neto)} pagados
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {cerrando && per && (
        <CierreNomina periodo={per} onClose={() => setCerrando(false)}
                      onListo={() => { cargar(); onListo(); }} />
      )}

      <Dialog open={!!ajuste} onOpenChange={(o) => !o && setAjuste(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-ajuste">
          <DialogHeader>
            <DialogTitle className="font-display">Ajustes de {ajuste?.nombre}</DialogTitle>
            <DialogDescription>
              Horas extras, bonificaciones y descuentos. El sistema recalcula AFP, SFS e ISR.
            </DialogDescription>
          </DialogHeader>
          {ajuste && (
            <div className="grid gap-4 sm:grid-cols-2">
              {[["horas_extras", "Horas extras"], ["valor_hora_extra", "Valor de la hora extra (RD$)"],
                ["bonificaciones", "Bonificaciones (RD$)"], ["otros_ingresos", "Otros ingresos (RD$)"],
                ["prestamos", "Préstamos (RD$)"], ["avances", "Avances (RD$)"],
                ["ausencias", "Ausencias y tardanzas (RD$)"], ["otros_descuentos", "Otros descuentos (RD$)"]]
                .map(([clave, label]) => (
                <div key={clave} className="space-y-2">
                  <Label>{label}</Label>
                  <Input type="number" value={ajuste[clave]} data-testid={`ajuste-${clave}`}
                         onChange={(e) => setAjuste({ ...ajuste, [clave]: e.target.value })} />
                </div>
              ))}
              <div className="space-y-2 sm:col-span-2">
                <Label>Nota</Label>
                <Input value={ajuste.nota} data-testid="ajuste-nota"
                       onChange={(e) => setAjuste({ ...ajuste, nota: e.target.value })} />
              </div>
              <Button className="rounded-full py-6 sm:col-span-2" onClick={guardarAjuste}
                      data-testid="guardar-ajuste">
                Recalcular volante
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const CheckSilencioso = null;
